/**
 * Code-defined vocabulary for feature flag keys (FLIP-304), mirroring
 * BrainChanger.Domain.Models.FeatureFlagModels.FeatureFlagKeys on the API side. Keep the two in
 * sync by hand - there are only ever a handful of these, so a generated/shared source isn't
 * worth the build complexity yet.
 */
export const FeatureFlagKeys = {
  /** Gates the onboarding walkthrough as a whole (FLIP-6). Individual lessons are not separately flagged in this first pass. */
  OnboardingWalkthrough: "onboarding-walkthrough",
  /**
   * Whether Community (NodeBB) notifications are shown and pushed (FLIP-477). Set by the
   * NodeBB:NotificationsEnabled app setting and merged into GetResolvedFlags by the server - it is not
   * a row in the FeatureFlags table - so the web and the webhook read the same switch.
   */
  CommunityNotifications: "community-notifications",
  /**
   * Whether the GTM plans (recurring Digital tier with a free trial) replace the beta one-time plans
   * on the payments page (FLIP-497). Set by the Stripe:GtmPlansEnabled app setting (Azure:
   * Stripe__GtmPlansEnabled) and merged into GetResolvedFlags by the server - it is not a row in the
   * FeatureFlags table. The server also refuses checkout for the plan set that is not on sale.
   */
  GtmPlans: "gtm-plans",
  /**
   * Gates Nova Insights as a whole (FLIP-527): the Insight widget, the Insights section of Settings and the N05
   * onboarding step. A FeatureFlags table row seeded OFF by migration; the server also refuses to generate
   * a card or send the Insights push while it is off. Turned on globally (DefaultEnabled) or per user (override).
   */
  NovaInsights: "nova-insights",
  /**
   * Gates the one-time "welcome to flip*" onboarding promo modal (FLIP-144). A FeatureFlags table row
   * seeded ON by migration (deploying changes nothing); turn it off globally (DefaultEnabled = false) or
   * per user (override). Nobody is marked as having seen the promo while it is off.
   */
  CommunityPromo: "community-promo",
} as const;

export type FeatureFlagKey = (typeof FeatureFlagKeys)[keyof typeof FeatureFlagKeys];

/**
 * Resolved when the boot-time fetch fails (offline, API error, not yet loaded) - see
 * FeatureFlagService.isEnabled. Deliberately conservative (everything off) rather than mirroring
 * each flag's server-side default, since a client that can't reach the API also can't know
 * whether a kill switch was just flipped off for exactly this reason.
 */
export const FEATURE_FLAG_FALLBACK_DEFAULTS: Record<FeatureFlagKey, boolean> = {
  [FeatureFlagKeys.OnboardingWalkthrough]: false,
  [FeatureFlagKeys.CommunityNotifications]: false,
  [FeatureFlagKeys.GtmPlans]: false,
  [FeatureFlagKeys.NovaInsights]: false,
  [FeatureFlagKeys.CommunityPromo]: false,
};
